import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { X, ChevronDown } from 'lucide-react'
import type { DocsCategory } from '@/routes/docs/_data'
import { DocsSearch } from './DocsSearch'

interface DocsSidebarProps {
  categories: DocsCategory[]
  mobileOpen: boolean
  onMobileClose: () => void
}

function CategoryGroup({ category }: { category: DocsCategory }) {
  const location = useLocation()
  const currentSlug = location.pathname.split('/docs/')[1] || 'introduction'
  const hasActiveChild = category.articles.some((a) => a.slug === currentSlug)
  const [open, setOpen] = useState<boolean>(hasActiveChild || true)

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
      >
        {category.label}
        <ChevronDown
          className={cn(
            'h-3 w-3 transition-transform duration-200',
            !open && '-rotate-90'
          )}
        />
      </button>
      {open && (
        <ul className="mt-0.5 space-y-px">
          {category.articles.map((article) => {
            const isActive = currentSlug === article.slug
            const Icon = article.icon
            return (
              <li key={article.slug}>
                <Link
                  to={`/docs/${article.slug}`}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-1.5 text-[13px] transition-colors border-l-2',
                    isActive
                      ? 'border-sky-500 text-foreground font-medium bg-sky-500/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40'
                  )}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span className="truncate">{article.title}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function SidebarContent({ categories, showSearch = false }: { categories: DocsCategory[]; showSearch?: boolean }) {
  return (
    <nav className="px-5 py-5 space-y-5">
      {showSearch && (
        <div className="pb-2">
          <DocsSearch />
        </div>
      )}
      {categories.map((category) => (
        <CategoryGroup key={category.label} category={category} />
      ))}
    </nav>
  )
}

export function DocsSidebar({ categories, mobileOpen, onMobileClose }: DocsSidebarProps) {
  return (
    <>
      <aside className="hidden lg:block w-[320px] shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto border-r border-border/40">
        <SidebarContent categories={categories} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-background shadow-2xl overflow-y-auto border-r border-border">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm font-semibold tracking-tight">Documentation</span>
              <button
                onClick={onMobileClose}
                className="p-1 rounded-md hover:bg-muted transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SidebarContent categories={categories} />
          </div>
        </div>
      )}
    </>
  )
}
